import clsx from "clsx";
import type { ComponentType, SVGProps } from "react";

import Tooltip from "~/components/Tooltip.tsx";
import { LoadingValue } from "~/modules/dashboard/components/LoadingValue.tsx";

type Props = {
	icon: ComponentType<SVGProps<SVGSVGElement>>;
	label: string;
	value: string;
	isLoading?: boolean;
	hint?: string;
	tooltip?: string;
	valueClassName?: string;
};

// While the value loads, `LoadingValue` takes its place with the size of the value line, so the row does
// not jump when the value arrives.
export function FigureRow({ icon: Icon, label, value, isLoading = false, hint, tooltip, valueClassName = "text-gray-900" }: Props) {
	return (
		<div className="flex items-center gap-3 py-1.5">
			<span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary-500 text-white">
				<Icon aria-hidden className="size-4" />
			</span>
			{/* The page is `lang="en"`; the label is German, so the hyphenation needs `de`. */}
			<dt lang="de"
				className={clsx(
					"min-w-0 flex-1 hyphens-auto text-base font-semibold text-gray-700 lg:text-sm",
				)}>{label}</dt>
			<dd className={clsx("text-xl font-semibold lg:text-base", valueClassName)}>
				{isLoading
					? <LoadingValue />
					: (
						<>
							{/* `tabIndex` makes the tooltip reachable with the keyboard. */}
							{tooltip ? <Tooltip text={tooltip}><span tabIndex={0}>{value}</span></Tooltip> : value}
							{hint && <span className="ml-2 text-xs font-normal text-gray-500">{hint}</span>}
						</>
					)}
			</dd>
		</div>
	);
}
